/**
 * M02 L1 prompt 灰度配置 · S2 Day 6b
 *
 * 数据源：
 * - useL1PromptABConfig(merchant_id) → GET /admin/v1/intent/merchants/{mid}/l1_prompt
 * - usePutL1PromptABConfig(merchant_id) → PUT 同路径
 *
 * 设计：
 * - 商户 ID 输入 → 触发查询；ratio 走 5%/20%/100% 推荐挡位 + 自由数字输入
 * - version_b 为空时强制 ratio=0（前端先拦 · 后端再校验 · 双锁）
 * - 错误回显走 API detail · 不重复实现 schema 约束
 */
import { useEffect, useMemo, useState } from "react";

import { useL1PromptABConfig, usePutL1PromptABConfig } from "@/lib/api/intent";

interface ApiErrorBody {
	detail?: string;
}

function extractErrorDetail(err: unknown): string {
	if (typeof err === "object" && err !== null) {
		const maybeAxios = err as { response?: { data?: ApiErrorBody }; message?: string };
		if (maybeAxios.response?.data?.detail) return maybeAxios.response.data.detail;
		if (maybeAxios.message) return maybeAxios.message;
	}
	return String(err);
}

const PRESET_RATIOS = [0, 5, 20, 100];

export function L1AbConfig() {
	const [merchantInput, setMerchantInput] = useState<string>("mer_devcons");
	const [activeMerchant, setActiveMerchant] = useState<string | null>("mer_devcons");

	const { data, isLoading, isError, error } = useL1PromptABConfig(activeMerchant);
	const putMutation = usePutL1PromptABConfig(activeMerchant ?? "");

	const [versionA, setVersionA] = useState<string>("");
	const [versionB, setVersionB] = useState<string>("");
	const [ratio, setRatio] = useState<number>(0);
	const [savedAt, setSavedAt] = useState<string | null>(null);
	const [submitErr, setSubmitErr] = useState<string | null>(null);

	useEffect(() => {
		if (data) {
			setVersionA(data.l1_prompt_version_a);
			setVersionB(data.l1_prompt_version_b ?? "");
			setRatio(data.l1_ab_ratio);
			setSubmitErr(null);
		}
	}, [data]);

	const versionBNormalized: string | null = versionB.trim() === "" ? null : versionB.trim();

	const localValidationError = useMemo<string | null>(() => {
		if (!versionA.trim()) return "version_a 必填";
		if (versionA.length > 20) return "version_a 长度上限 20";
		if (versionBNormalized !== null && versionBNormalized.length > 20)
			return "version_b 长度上限 20";
		if (ratio < 0 || ratio > 100) return "ratio 范围 0-100";
		if (versionBNormalized === null && ratio !== 0) return "version_b 为空时 ratio 必须为 0";
		return null;
	}, [versionA, versionBNormalized, ratio]);

	const handleQuery = () => {
		const trimmed = merchantInput.trim();
		setActiveMerchant(trimmed === "" ? null : trimmed);
		setSavedAt(null);
		setSubmitErr(null);
	};

	const handleSubmit = () => {
		if (!activeMerchant) return;
		setSubmitErr(null);
		setSavedAt(null);
		putMutation.mutate(
			{
				l1_prompt_version_a: versionA.trim(),
				l1_prompt_version_b: versionBNormalized,
				l1_ab_ratio: ratio,
			},
			{
				onSuccess: () => setSavedAt(new Date().toLocaleTimeString()),
				onError: (err) => setSubmitErr(extractErrorDetail(err)),
			},
		);
	};

	return (
		<div className="space-y-4" data-testid="l1-ab-config">
			<div className="flex flex-wrap items-end gap-2">
				<label className="flex flex-col text-sm">
					<span className="mb-1 text-text-secondary">商户 ID</span>
					<input
						type="text"
						value={merchantInput}
						onChange={(e) => setMerchantInput(e.target.value)}
						placeholder="mer_xxx"
						className="rounded-md border border-surface-border bg-surface-card px-3 py-1.5 text-sm font-mono"
						data-testid="l1-ab-merchant-input"
						maxLength={12}
					/>
				</label>
				<button
					type="button"
					className="rounded-md border border-surface-border px-3 py-1.5 text-sm hover:bg-surface-subtle"
					data-testid="l1-ab-merchant-load"
					onClick={handleQuery}
				>
					加载
				</button>
			</div>

			{!activeMerchant ? (
				<p className="text-sm text-text-muted">输入商户 ID 后点"加载"。</p>
			) : isLoading ? (
				<p data-testid="l1-ab-loading" className="text-sm text-text-secondary">
					加载中...
				</p>
			) : isError ? (
				<p data-testid="l1-ab-error" className="text-sm text-brand-danger">
					加载失败：{extractErrorDetail(error)}
				</p>
			) : data ? (
				<form
					className="space-y-3 rounded-md border border-surface-border p-4"
					onSubmit={(e) => {
						e.preventDefault();
						handleSubmit();
					}}
					data-testid="l1-ab-form"
				>
					<div className="text-xs text-text-muted">
						最近更新 {new Date(data.updated_at).toLocaleString()}
					</div>
					<label className="block text-sm">
						<span className="mb-1 block text-text-secondary">主版本（必填）</span>
						<input
							type="text"
							value={versionA}
							onChange={(e) => setVersionA(e.target.value)}
							className="w-48 rounded-md border border-surface-border bg-surface-card px-3 py-1.5 font-mono text-sm"
							data-testid="l1-ab-version-a"
							maxLength={20}
						/>
					</label>
					<label className="block text-sm">
						<span className="mb-1 block text-text-secondary">灰度版本（留空 = 关闭灰度）</span>
						<input
							type="text"
							value={versionB}
							onChange={(e) => setVersionB(e.target.value)}
							className="w-48 rounded-md border border-surface-border bg-surface-card px-3 py-1.5 font-mono text-sm"
							data-testid="l1-ab-version-b"
							maxLength={20}
							placeholder="空 = 不灰度"
						/>
					</label>
					<div className="text-sm">
						<div className="mb-1 text-text-secondary">B 流量占比（0-100）</div>
						<div className="flex flex-wrap items-center gap-2">
							{PRESET_RATIOS.map((p) => (
								<button
									key={p}
									type="button"
									className={`rounded-md border px-2 py-1 text-xs ${
										ratio === p
											? "border-brand-accent bg-brand-accent/10 text-brand-accent"
											: "border-surface-border hover:bg-surface-subtle"
									}`}
									data-testid={`l1-ab-ratio-preset-${p}`}
									onClick={() => setRatio(p)}
								>
									{p}%
								</button>
							))}
							<input
								type="number"
								value={ratio}
								onChange={(e) => setRatio(Number.parseInt(e.target.value || "0", 10))}
								className="w-24 rounded-md border border-surface-border bg-surface-card px-3 py-1.5 font-mono text-sm"
								min={0}
								max={100}
								data-testid="l1-ab-ratio-input"
							/>
							<span className="text-xs text-text-muted">% 使用灰度版本，其余使用主版本</span>
						</div>
					</div>

					{localValidationError ? (
						<p data-testid="l1-ab-local-error" className="text-xs text-brand-danger">
							{localValidationError}
						</p>
					) : null}

					<div className="flex items-center gap-3">
						<button
							type="submit"
							className="rounded-md border border-brand-accent bg-brand-accent px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
							data-testid="l1-ab-submit"
							disabled={putMutation.isPending || localValidationError !== null}
						>
							{putMutation.isPending ? "保存中..." : "保存"}
						</button>
						{savedAt ? (
							<span
								data-testid="l1-ab-saved-at"
								className="rounded border border-brand-success/30 bg-brand-success/10 px-2 py-0.5 text-xs text-brand-success"
							>
								已保存 {savedAt}
							</span>
						) : null}
					</div>

					{submitErr ? (
						<p
							data-testid="l1-ab-submit-error"
							className="rounded-md border border-brand-danger bg-brand-danger/10 p-2 text-xs text-brand-danger"
						>
							{submitErr}
						</p>
					) : null}
				</form>
			) : null}

			<p className="text-xs text-text-muted">
				灰度推荐挡位为 5%/20%/100%；灰度版本为空时会自动关闭灰度。
			</p>
		</div>
	);
}
